import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { bustCache } from '../services/authApi';
import {
  ArrowLeft,
  Search,
  AlertCircle,
  Calendar,
  User,
  FileText,
  AlertTriangle,
  Eye,
  Edit2,
  X
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const AssignProblemsEmergenciesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  
  // Multi-problem selection state
  const [isMultiProblemMode, setIsMultiProblemMode] = useState(false);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedProblemId, setSelectedProblemId] = useState('');
  const [notes, setNotes] = useState('');
  const [performedDate, setPerformedDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [problemSearchTerm, setProblemSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Preview/Edit modal state
  const [previewingProblem, setPreviewingProblem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  
  const categories = ['All', 'General', 'Surgical', 'Restorative', 'Periodontics', 'Orthodontics', 'Prosthodontics', 'Endodontics', 'Cosmetic'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      
      const [patientsRes, problemsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/practice/patients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      setPatients(patientsRes.data.data || []);
      setProblems(problemsRes.data.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleProblemSelection = (problemId) => {
    const newSelected = new Set(selectedProblems);
    if (newSelected.has(problemId)) {
      newSelected.delete(problemId);
    } else {
      newSelected.add(problemId);
    }
    setSelectedProblems(newSelected);
  };

  const handlePreview = async (problem, editMode = false) => {
    try {
      // Fetch full details
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library/${problem.id}/base`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const fullProblem = response.data.data;
        setPreviewingProblem(fullProblem);
        setEditedContent(fullProblem.overview || '');
        setIsEditMode(editMode);
      }
    } catch (error) {
      console.error('Failed to fetch problem details:', error);
      toast({
        title: "Error",
        description: "Failed to load problem details",
        variant: "destructive"
      });
    }
  };

  const closePreview = () => {
    setPreviewingProblem(null);
    setIsEditMode(false);
    setEditedContent('');
  };

  const handleAssign = async () => {
    if (!selectedPatientId) {
      toast({
        title: "Validation Error",
        description: "Please select a patient",
        variant: "destructive"
      });
      return;
    }

    if (isMultiProblemMode) {
      if (selectedProblems.size === 0) {
        toast({
          title: "Validation Error",
          description: "Please select at least one problem/emergency",
          variant: "destructive"
        });
        return;
      }
    } else {
      if (!selectedProblemId) {
        toast({
          title: "Validation Error",
          description: "Please select a problem/emergency",
          variant: "destructive"
        });
        return;
      }
    }

    try {
      setAssigning(true);
      const token = localStorage.getItem('dentalToken');
      const selectedPatient = patients.find(p => p.id === selectedPatientId);

      if (isMultiProblemMode) {
        // Multi-problem assignment
        const problemsList = Array.from(selectedProblems).map(probId => {
          const problem = problems.find(p => p.id === probId);
          return {
            problem_id: probId,
            problem_name: problem.name
          };
        });

        for (const prob of problemsList) {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/assign`,
            {
              patient_id: selectedPatientId,
              problem_id: prob.problem_id,
              problem_name: prob.problem_name,
              notes: notes || null,
              performed_date: performedDate
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        }

        toast({
          title: "Success!",
          description: `${problemsList.length} problems/emergencies assigned to ${selectedPatient.firstName} ${selectedPatient.lastName}`,
          variant: "default"
        });
      } else {
        // Single problem assignment
        const selectedProblem = problems.find(p => p.id === selectedProblemId);

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/assign`,
          {
            patient_id: selectedPatientId,
            problem_id: selectedProblemId,
            problem_name: selectedProblem.name,
            notes: notes || null,
            performed_date: performedDate
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        toast({
          title: "Success!",
          description: `${selectedProblem.name} assigned to ${selectedPatient.firstName} ${selectedPatient.lastName}`,
          variant: "default"
        });
      }

      // Navigate back after delay
      bustCache('dashboard');
      setTimeout(() => {
        navigate('/');
      }, 1500);

    } catch (error) {
      console.error('Assignment failed:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to assign problem/emergency",
        variant: "destructive"
      });
    } finally {
      setAssigning(false);
    }
  };

  const filteredPatients = patients.filter(patient =>
    `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
    patient.email?.toLowerCase().includes(patientSearchTerm.toLowerCase())
  );

  const filteredProblems = problems.filter(problem => {
    const matchesSearch = problem.name.toLowerCase().includes(problemSearchTerm.toLowerCase()) ||
                         problem.category.toLowerCase().includes(problemSearchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || problem.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  const selectedProblem = problems.find(p => p.id === selectedProblemId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mr-4"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Assign Problems & Emergencies</h1>
                <p className="text-gray-600 mt-1">Assign emergency care instructions to patients</p>
              </div>
            </div>
            <Button
              variant={isMultiProblemMode ? "default" : "outline"}
              onClick={() => {
                setIsMultiProblemMode(!isMultiProblemMode);
                setSelectedProblems(new Set());
                setSelectedProblemId('');
              }}
              className="ml-4"
            >
              {isMultiProblemMode ? 'Multi-Select ON' : 'Enable Multi-Select'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Patient Selection */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Step 1: Select A Patient
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      type="text"
                      placeholder="Type patient name to search..."
                      value={patientSearchTerm}
                      onChange={(e) => setPatientSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredPatients.map(patient => (
                    <div
                      key={patient.id}
                      onClick={() => setSelectedPatientId(patient.id)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedPatientId === patient.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p className="text-sm text-gray-600">{patient.email || 'No email'}</p>
                        </div>
                        {selectedPatientId === patient.id && (
                          <Badge className="bg-blue-600">Selected</Badge>
                        )}
                      </div>
                    </div>
                  ))}

                  {filteredPatients.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <User className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p>No patients found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Problem Selection */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-red-600" />
                  Step 2: Select Problem/Emergency {isMultiProblemMode && `(${selectedProblems.size} selected)`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      type="text"
                      placeholder="Search problems/emergencies..."
                      value={problemSearchTerm}
                      onChange={(e) => setProblemSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {categories.map(category => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredProblems.map(problem => (
                    <div
                      key={problem.id}
                      onClick={() => {
                        if (isMultiProblemMode) {
                          toggleProblemSelection(problem.id);
                        } else {
                          setSelectedProblemId(problem.id);
                        }
                      }}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        (isMultiProblemMode && selectedProblems.has(problem.id)) || 
                        (!isMultiProblemMode && selectedProblemId === problem.id)
                          ? 'border-red-600 bg-red-50'
                          : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{problem.name}</p>
                          <Badge variant="secondary" className="mt-1">
                            {problem.category}
                          </Badge>
                        </div>
                        {((isMultiProblemMode && selectedProblems.has(problem.id)) ||
                          (!isMultiProblemMode && selectedProblemId === problem.id)) && (
                          <Badge className="bg-red-600">Selected</Badge>
                        )}
                      </div>
                    </div>
                  ))}

                  {filteredProblems.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p>No problems/emergencies found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Step 3: Preview/Edit Selected Problem */}
        {selectedPatientId && selectedProblemId && !isMultiProblemMode && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Edit2 className="h-5 w-5 mr-2 text-purple-600" />
                Step 3: Review & Edit (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  {selectedProblem?.name}
                </h3>
                <Badge variant="secondary">{selectedProblem?.category}</Badge>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => handlePreview(selectedProblem, false)}
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Content
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePreview(selectedProblem, true)}
                  className="flex-1"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit/Customize Content
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                Preview to see the full content, or Edit to create a customized version for your practice before assigning.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Assignment Details */}
        {(selectedPatientId && (selectedProblemId || selectedProblems.size > 0)) && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" />
                Step 4: Assignment Details (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    Date Assigned
                  </label>
                  <Input
                    type="date"
                    value={performedDate}
                    onChange={(e) => setPerformedDate(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special instructions or notes..."
                  rows={3}
                  className="w-full"
                />
              </div>

              {/* Summary */}
              {selectedPatient && (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertDescription className="text-blue-900">
                    <strong>Ready to assign:</strong>
                    <br />
                    {isMultiProblemMode ? (
                      <span>{selectedProblems.size} problems/emergencies</span>
                    ) : (
                      <span className="font-medium">{selectedProblem?.name}</span>
                    )} to{' '}
                    <span className="font-medium">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </span>
                  </AlertDescription>
                </Alert>
              )}

              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-green-600" />
                  Step 5: Assign
                </h3>
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedPatientId('');
                      setSelectedProblemId('');
                      setSelectedProblems(new Set());
                      setNotes('');
                      setPerformedDate(new Date().toISOString().split('T')[0]);
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleAssign}
                    disabled={!selectedPatientId || (!selectedProblemId && selectedProblems.size === 0) || assigning}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {assigning ? 'Assigning...' : '✓ Assign to Patient'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Preview/Edit Modal */}
      {previewingProblem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isEditMode ? 'Edit' : 'Preview'}: {previewingProblem.name}
                </h2>
                <Badge variant="secondary" className="mt-2">
                  {previewingProblem.category}
                </Badge>
              </div>
              <button
                onClick={closePreview}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isEditMode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Edit Content (this will create your practice-specific version)
                  </label>
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    rows={20}
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Character count: {editedContent.length}
                  </p>
                </div>
              ) : (
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-gray-700 leading-relaxed">
                    {previewingProblem.overview}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t p-6">
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={closePreview}
                >
                  Close
                </Button>
                {isEditMode && (
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('dentalToken');
                        await axios.put(
                          `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library/${previewingProblem.id}`,
                          { overview: editedContent },
                          {
                            headers: { 'Authorization': `Bearer ${token}` }
                          }
                        );
                        
                        toast({
                          title: "Success",
                          description: "Practice-specific customization saved",
                        });
                        
                        closePreview();
                        loadData(); // Reload to show updated version
                      } catch (error) {
                        console.error('Failed to save edits:', error);
                        toast({
                          title: "Error",
                          description: "Failed to save customization",
                          variant: "destructive"
                        });
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Save Customization
                  </Button>
                )}
                {!isEditMode && (
                  <Button
                    onClick={() => setIsEditMode(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Switch to Edit Mode
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignProblemsEmergenciesPage;
